import { createAsyncThunk } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { loadInteractionsByProject } from 'store/interactions/thunks/loadInteractionsByProject';
import { selectProjectUsers } from 'store/projectUsers/selectors/projectUsers.selectors';
import { loadUsersAndPersonalResourcesByUserIds } from 'store/projectUsers/thunks/loadUsersAndPersonalResourcesByUserIds';

/**
 * The distinct, non-empty project ids of `projectIds` whose interactions the
 * bank has not loaded (or started loading) in this bank session — see
 * `interactionsLoadedProjectIds` in the bank slice, which is reset with the
 * scene.
 * @param {Object} state redux state
 * @param {Array<string>} projectIds project ids of the files of a folder
 * @returns {Array<string>} project ids still to load
 */
export const getUnloadedInteractionProjectIds = (state, projectIds) => {
  const loaded =
    (state.projectBank && state.projectBank.interactionsLoadedProjectIds) || {};
  return uniq((projectIds || []).filter(Boolean)).filter((id) => !loaded[id]);
};

/**
 * Loads the interactions (reactions, comments) of every given project into
 * the shared interactions slice — one `GET interactions/by-project/:id` per
 * project — and then the users who reacted but are not yet in projectUsers,
 * so the reaction chips can name them. Dispatched by
 * loadProjectBankFolderContents for the project ids of the loaded files that
 * getUnloadedInteractionProjectIds still reports; the slice marks the ids
 * loaded on `pending` and un-marks the ones that failed on `fulfilled` /
 * `rejected`, so a failed project is retried on the next folder open.
 * Resolves `{ projectIds, failedProjectIds }`.
 */
export const loadProjectBankInteractions = createAsyncThunk(
  'projectBank/loadProjectBankInteractions',
  async (projectIds, { dispatch, getState }) => {
    const results = await Promise.all(
      projectIds.map((id) => dispatch(loadInteractionsByProject(id)))
    );
    const failedProjectIds = projectIds.filter(
      (_, index) => !!results[index].error
    );

    const reactorIds = uniq(
      results
        .flatMap((result) =>
          Array.isArray(result.payload) ? result.payload : []
        )
        .map((interaction) => interaction && interaction.userId)
        .filter(Boolean)
    );
    const known = selectProjectUsers(getState()) || {};
    const missing = reactorIds.filter((id) => !known[id]);
    if (missing.length > 0) {
      dispatch(loadUsersAndPersonalResourcesByUserIds(missing));
    }

    return { projectIds, failedProjectIds };
  }
);
